import React, { useContext, useEffect, useLayoutEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { AdminModeProvider } from './context/AdminModeContext';
import { Header } from './components/Header';
import { AdminModeBanner } from './components/AdminModeBanner';
import { VerificationBanner } from './components/VerificationBanner';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { UnverifiedActionModal } from './components/UnverifiedActionModal';
import { CommandPalette } from './components/CommandPalette';
import { SWUpdatePrompt } from './components/SWUpdatePrompt';
import { Footer } from './components/Footer';

// Wrapper around React.lazy that detects "stale chunk after deploy"
// and reloads the page once. Two failure modes the wrapper catches:
//   - The dynamic import fetch fails (network error or 4xx).
//   - The chunk arrives but its bytes start with `<` (Vercel's SPA
//     fallback returned index.html because the old hashed chunk was
//     deleted from the deploy artifact). React then throws a
//     SyntaxError while parsing the "JS" — which is HTML.
// Both happen when a user has the SPA loaded during a redeploy and
// then navigates to a route they hadn't visited yet. Without this,
// the user lands on a permanent error boundary; with it, one auto-
// reload pulls the new bundle and they continue. The sessionStorage
// guard prevents a reload-loop if the failure isn't actually about
// a stale chunk.
const RELOAD_COOLDOWN_MS = 30_000;
function lazyWithReload(importFn) {
  return lazy(() => importFn().catch((err) => {
    const msg = String(err?.message || err);
    const looksLikeStaleChunk = /Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|Unexpected token '<'/.test(msg);
    if (!looksLikeStaleChunk) throw err;
    // 30s cooldown: a second consecutive failure within the window
    // means reloading didn't fix it — surface the real error rather
    // than loop. After 30s we assume any new failure is a fresh
    // deploy event and reload again.
    try {
      const KEY = 'owndecks:chunk-reload-at';
      const last = parseInt(window.sessionStorage.getItem(KEY) || '0', 10);
      if (last && Date.now() - last < RELOAD_COOLDOWN_MS) throw err;
      window.sessionStorage.setItem(KEY, String(Date.now()));
    } catch { /* private mode — fall through to reload anyway */ }
    window.location.reload();
    // Suspend the import forever so React keeps the Suspense fallback
    // visible until the page actually reloads.
    return new Promise(() => {});
  }));
}

// Public
const Login           = lazyWithReload(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register        = lazyWithReload(() => import('./pages/Register').then(m => ({ default: m.Register })));
const AccountPending  = lazyWithReload(() => import('./pages/AccountPending').then(m => ({ default: m.AccountPending })));
const AccountRejected = lazyWithReload(() => import('./pages/AccountRejected').then(m => ({ default: m.AccountRejected })));
const Home         = lazyWithReload(() => import('./pages/Home').then(m => ({ default: m.Home })));
const ArtistDetail = lazyWithReload(() => import('./pages/ArtistDetail').then(m => ({ default: m.ArtistDetail })));
const SearchPage   = lazyWithReload(() => import('./pages/SearchPage').then(m => ({ default: m.SearchPage })));
const FeedPage     = lazyWithReload(() => import('./pages/FeedPage').then(m => ({ default: m.FeedPage })));
const FillIn       = lazyWithReload(() => import('./pages/FillIn').then(m => ({ default: m.FillIn })));
const PublicGig    = lazyWithReload(() => import('./pages/PublicGig').then(m => ({ default: m.PublicGig })));
const GigDay       = lazyWithReload(() => import('./pages/GigDay').then(m => ({ default: m.GigDay })));
const Venue        = lazyWithReload(() => import('./pages/Venue').then(m => ({ default: m.Venue })));
const ArtistEPK    = lazyWithReload(() => import('./pages/ArtistEPK').then(m => ({ default: m.ArtistEPK })));
const Admin        = lazyWithReload(() => import('./pages/Admin').then(m => ({ default: m.Admin })));
const ForgotPassword = lazyWithReload(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword  = lazyWithReload(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const VerifyEmail    = lazyWithReload(() => import('./pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const CrewsBrowse    = lazyWithReload(() => import('./pages/CrewsBrowse').then(m => ({ default: m.CrewsBrowse })));
const CrewDetail     = lazyWithReload(() => import('./pages/CrewDetail').then(m => ({ default: m.CrewDetail })));
const Terms          = lazyWithReload(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy        = lazyWithReload(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const TrustSafety    = lazyWithReload(() => import('./pages/TrustSafety').then(m => ({ default: m.TrustSafety })));
const SceneDublin    = lazyWithReload(() => import('./pages/SceneDublin').then(m => ({ default: m.SceneDublin })));
const Inquiries      = lazyWithReload(() => import('./pages/Inquiries').then(m => ({ default: m.Inquiries })));
const Pro            = lazyWithReload(() => import('./pages/Pro').then(m => ({ default: m.Pro })));

// Artist Platform
const ArtistDashboard  = lazyWithReload(() => import('./pages/artist-platform/ArtistDashboard').then(m => ({ default: m.ArtistDashboard })));
const ProfileHub       = lazyWithReload(() => import('./pages/artist-platform/ProfileHub').then(m => ({ default: m.ProfileHub })));
const ArtistBookings   = lazyWithReload(() => import('./pages/artist-platform/ArtistBookings').then(m => ({ default: m.ArtistBookings })));
const ArtistAnalytics  = lazyWithReload(() => import('./pages/artist-platform/ArtistAnalytics').then(m => ({ default: m.ArtistAnalytics })));
const ArtistEarnings   = lazyWithReload(() => import('./pages/artist-platform/ArtistEarnings').then(m => ({ default: m.ArtistEarnings })));
// Replaces the prior artist-only ArtistCommunity page. The new
// Community surface is shared: artists post, organisers + artists
// comment + like. Lives in src/pages/Community.js but the bulk of
// the feature is in src/community/ (PostFeed, PostCard, CreatePost,
// CommentList, CommentInput).
const Community       = lazyWithReload(() => import('./pages/Community').then(m => ({ default: m.Community })));
const ArtistSettings   = lazyWithReload(() => import('./pages/artist-platform/ArtistSettings').then(m => ({ default: m.ArtistSettings })));

// Organizer Platform
const OrganizerBookings = lazyWithReload(() => import('./pages/organizer-platform/OrganizerBookings').then(m => ({ default: m.OrganizerBookings })));
const OrganizerAnalytics = lazyWithReload(() => import('./pages/organizer-platform/OrganizerAnalytics').then(m => ({ default: m.OrganizerAnalytics })));
const OrganizerTeam     = lazyWithReload(() => import('./pages/organizer-platform/OrganizerTeam').then(m => ({ default: m.OrganizerTeam })));
const OrganizerPayments = lazyWithReload(() => import('./pages/organizer-platform/OrganizerPayments').then(m => ({ default: m.OrganizerPayments })));
const OrganizerSettings = lazyWithReload(() => import('./pages/organizer-platform/OrganizerSettings').then(m => ({ default: m.OrganizerSettings })));
const OrganizerCRM      = lazyWithReload(() => import('./pages/organizer-platform/OrganizerCRM').then(m => ({ default: m.OrganizerCRM })));
const FindArtists       = lazyWithReload(() => import('./pages/organizer-platform/FindArtists').then(m => ({ default: m.FindArtists })));

// Platform navs are role-gated chrome — only logged-in artists or
// organisers ever render them. Lazy-load them out of the main bundle
// so logged-out marketing/legal/auth visitors don't ship the axios
// + service-method stack the navs pull in transitively.
const ArtistPlatformNav    = lazyWithReload(() => import('./components/ArtistPlatformNav').then(m => ({ default: m.ArtistPlatformNav })));
const OrganizerPlatformNav = lazyWithReload(() => import('./components/OrganizerPlatformNav').then(m => ({ default: m.OrganizerPlatformNav })));

// Install + push hints are auth-gated nudges — already-installed users
// or denied-permission users never see them. Lazy-load so the localStorage
// + matchMedia checks + module body don't sit in main.js for everyone.
const InstallHint = lazyWithReload(() => import('./components/InstallHint').then(m => ({ default: m.InstallHint })));
const PushHint    = lazyWithReload(() => import('./components/PushHint').then(m => ({ default: m.PushHint })));

const PageLoader = () => (
  <div className="min-h-dvh bg-transparent flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-line-strong border-t-acid rounded-full animate-spin" />
  </div>
);

// Last-resort fallback so a render-time crash anywhere in the route tree
// shows recovery UI instead of a blank white screen. Per-route boundaries
// would be nicer; this is the minimum that keeps a tester unstuck.
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('App crashed:', error, info); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-dvh bg-night text-fg flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-fg mb-2">Something went wrong</h1>
          <p className="text-fg-muted mb-6">Try reloading the page. If it keeps happening, email <a href="mailto:hello@owndecks.com" className="underline hover:text-acid">hello@owndecks.com</a>.</p>
          <button
            onClick={() => window.location.assign('/')}
            className="px-6 py-3 bg-acid text-gray-900 rounded-lg font-semibold hover:bg-acid-400"
          >
            Reload home
          </button>
        </div>
      </div>
    );
  }
}

// FadeMain — wraps `<main>` and replays the fade-in animation on every
// route change WITHOUT remounting children. The previous version used
// `key={location.pathname}` on `<main>`, which forced a full subtree
// unmount/mount cycle on every nav: lazy-chunk Suspense fallbacks
// flashed even for already-imported routes, module-level state was
// thrown away, and ArtistDetail's 80-hook tree paid the cost twice.
//
// Implementation: useLayoutEffect removes `animate-fade-in`, forces a
// reflow (the `void offsetWidth` line), and re-adds the class — that's
// enough to restart the CSS animation without unmount.
const FadeMain = ({ isPlatform, pathname, children }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.classList.remove('animate-fade-in');
    // Force reflow so the browser registers the class removal before
    // we re-add it — without this the animation doesn't restart.
    void node.offsetWidth;
    node.classList.add('animate-fade-in');
  }, [pathname]);
  return (
    <main
      id="main"
      ref={ref}
      className={`animate-fade-in bg-night text-fg min-h-dvh ${isPlatform ? 'pb-[calc(env(safe-area-inset-bottom)+5.5rem)] md:pb-0' : ''}`}
    >
      {children}
    </main>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // `instant` instead of the implicit smooth from html { scroll-behavior:
    // smooth } — a 700ms scroll animation between routes blocks layout
    // work on low-end Android and feels broken when the new page hasn't
    // mounted yet.
    window.scrollTo({ top: 0, behavior: 'instant' });
    // Move accessible focus to the <main> landmark so VoiceOver/TalkBack
    // rotor users hear the new page; without this they stay on whatever
    // element they were on pre-navigation, which silently strands them.
    const main = document.getElementById('main');
    if (main) {
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
      main.removeAttribute('tabindex');
    }
  }, [pathname]);
  return null;
};

// Role-protected paths only. Anything publicly accessible (even if commonly
// used by one role) goes into isSharedPath below so logged-out visitors get
// the public Header, not a platform nav.
const ARTIST_PATHS = ['/dashboard', '/artist-bookings', '/profile', '/artist-analytics', '/artist-earnings', '/artist-settings', '/availability', '/rider'];
const ORGANIZER_PATHS = ['/organizer-bookings', '/analytics', '/payments', '/team', '/organizer-settings', '/crm', '/find-artists', '/find-djs'];

const ProtectedRoute = ({ children, requiredRole, requireAdmin }) => {
  const { user, loading } = useContext(AuthContext);
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  if (requiredRole && user.userType !== requiredRole) return <Navigate to="/" />;
  // requireAdmin (Issue #4) — defence against a non-admin landing on
  // /admin (deep link, stale URL, accidental tap). Without this guard
  // the Admin component renders briefly before the backend 403s flood
  // the data fetches — visible as a "flash of admin page" during route
  // transitions on mobile. user.isAdmin is the authoritative bit
  // (refreshed from /me on every AuthContext mount).
  if (requireAdmin && !user.isAdmin) return <Navigate to="/" />;
  return children;
};


const AppContent = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();

  // Shared paths use prefix matching so deep links like /crew/:slug, /artist/:id,
  // /gig/:id keep the platform nav for logged-in users (continuity inside the
  // platform). Logged-out visitors still get the public Header on these because
  // the !!user check below gates that.
  const SHARED_PREFIXES = ['/feed', '/fill-in', '/admin', '/crews', '/crew', '/community', '/search', '/dublin', '/inquiries', '/pro', '/artist', '/gig', '/gig-day', '/venue'];
  const isSharedPath = SHARED_PREFIXES.some(
    p => location.pathname === p || location.pathname.startsWith(p + '/')
  );
  // Defence-in-depth: even if a path appears in a role list, only render
  // the platform nav when an authenticated user actually exists. Logged-out
  // visitors always get the public Header.
  const isArtistPlatform = !!user && (
    ARTIST_PATHS.some(p => location.pathname.startsWith(p)) ||
    (isSharedPath && user.userType === 'artist')
  );
  const isOrganizerPlatform = !!user && (
    ORGANIZER_PATHS.some(p => location.pathname.startsWith(p)) ||
    (isSharedPath && user.userType === 'organizer')
  );

  const showFooter = !isArtistPlatform && !isOrganizerPlatform;

  return (
    <>
      <ScrollToTop />
      <ImpersonationBanner />
      <UnverifiedActionModal />
      <AdminModeBanner />
      <CommandPalette />
      <Suspense fallback={null}>
        <InstallHint />
        <PushHint />
      </Suspense>
      <SWUpdatePrompt />
      {isArtistPlatform && (
        <Suspense fallback={null}><ArtistPlatformNav /></Suspense>
      )}
      {isOrganizerPlatform && (
        <Suspense fallback={null}><OrganizerPlatformNav /></Suspense>
      )}
      {!isArtistPlatform && !isOrganizerPlatform && <Header />}
      <VerificationBanner />

      <Suspense fallback={<PageLoader />}>
        <FadeMain
          isPlatform={isArtistPlatform || isOrganizerPlatform}
          pathname={location.pathname}
        >
          <Routes>
            {/* Public */}
            <Route path="/login"            element={<Login />} />
            <Route path="/register"         element={<Register />} />
            <Route path="/account-pending"  element={<AccountPending />} />
            <Route path="/account-rejected" element={<AccountRejected />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password"  element={<ResetPassword />} />
            <Route path="/verify-email"    element={<VerifyEmail />} />
            <Route path="/"           element={<Home />} />
            <Route path="/artist/:id" element={<ArtistDetail />} />
            {/* Press kit page — canonical URL is /press-kit; the old
                /epk path is kept as an alias so any link sent to a
                promoter still works. EPK (Electronic Press Kit) is
                industry shorthand; "Press kit" is what we surface
                everywhere user-visible. */}
            <Route path="/artist/:id/press-kit" element={<ArtistEPK />} />
            <Route path="/artist/:id/epk" element={<ArtistEPK />} />
            <Route path="/gig/:id" element={<PublicGig />} />
            <Route path="/gig-day/:id" element={<ProtectedRoute><GigDay /></ProtectedRoute>} />
            <Route path="/venue/:slug" element={<Venue />} />
            {/* Browse / discovery surfaces (Crews list, Find artists,
                Scene) are platform entry points — gated behind auth so
                logged-out visitors only see the marketing homepage and
                the off-platform sharing surfaces (artist profile, gig,
                venue, press kit). */}
            <Route path="/crews" element={<ProtectedRoute><CrewsBrowse /></ProtectedRoute>} />
            <Route path="/crew/:slug" element={<ProtectedRoute><CrewDetail /></ProtectedRoute>} />
            {/* /search (the open browse) is organiser-only — its filter
                UI (genre / vibe / fee / availability) is built around
                the booking flow, and the artist nav has no entry to it.
                Artists who want peer discovery use /community + /feed.
                Pairs with the requiredRole="organizer" guard on
                /find-artists below; both find-an-artist surfaces are
                organiser-scoped. */}
            <Route path="/search"     element={<ProtectedRoute requiredRole="organizer"><SearchPage /></ProtectedRoute>} />
            <Route path="/terms"      element={<Terms />} />
            <Route path="/privacy"    element={<Privacy />} />
            <Route path="/trust"      element={<TrustSafety />} />
            <Route path="/trust-safety" element={<TrustSafety />} />
            <Route path="/dublin"     element={<ProtectedRoute><SceneDublin /></ProtectedRoute>} />

            {/* Artist Platform */}
            <Route path="/dashboard"        element={<ProtectedRoute requiredRole="artist"><ArtistDashboard /></ProtectedRoute>} />
            <Route path="/artist-bookings"  element={<ProtectedRoute requiredRole="artist"><ArtistBookings /></ProtectedRoute>} />
            <Route path="/profile"          element={<ProtectedRoute requiredRole="artist"><ProfileHub /></ProtectedRoute>} />
            {/* Community is shared (read-only for anyone, post for
                artists, comment+like for authed) — no role gate, but
                still ProtectedRoute so anonymous viewers bounce to
                /login first and we don't have to gate every action
                inside the feed. */}
            <Route path="/community"        element={<ProtectedRoute><Community /></ProtectedRoute>} />
            <Route path="/artist-analytics" element={<ProtectedRoute requiredRole="artist"><ArtistAnalytics /></ProtectedRoute>} />
            <Route path="/artist-earnings"  element={<ProtectedRoute requiredRole="artist"><ArtistEarnings /></ProtectedRoute>} />
            <Route path="/artist-settings"  element={<ProtectedRoute requiredRole="artist"><ArtistSettings /></ProtectedRoute>} />
            {/* Old standalone routes redirect into the consolidated Profile hub. */}
            <Route path="/availability"     element={<Navigate to="/profile?tab=availability" replace />} />
            <Route path="/rider"            element={<Navigate to="/profile?tab=rider" replace />} />

            {/* Organizer Platform */}
            {/* /browse retired — redirect any old links to the merged /search surface */}
            <Route path="/browse" element={<Navigate to="/search" replace />} />
            <Route path="/organizer-bookings"  element={<ProtectedRoute requiredRole="organizer"><OrganizerBookings /></ProtectedRoute>} />
            <Route path="/analytics"           element={<ProtectedRoute requiredRole="organizer"><OrganizerAnalytics /></ProtectedRoute>} />
            <Route path="/payments"            element={<ProtectedRoute requiredRole="organizer"><OrganizerPayments /></ProtectedRoute>} />
            <Route path="/team"                element={<ProtectedRoute requiredRole="organizer"><OrganizerTeam /></ProtectedRoute>} />
            <Route path="/organizer-settings"  element={<ProtectedRoute requiredRole="organizer"><OrganizerSettings /></ProtectedRoute>} />
            <Route path="/crm"                 element={<ProtectedRoute requiredRole="organizer"><OrganizerCRM /></ProtectedRoute>} />
            <Route path="/find-artists"        element={<ProtectedRoute requiredRole="organizer"><FindArtists /></ProtectedRoute>} />
            {/* Legacy URL kept alive for emails sent before the rename and
                any deep-link bookmarks. Redirect is permanent — keep until
                the audit shows zero traffic to /find-djs. */}
            <Route path="/find-djs"            element={<Navigate to="/find-artists" replace />} />

            {/* Shared */}
            <Route path="/feed" element={<ProtectedRoute><FeedPage /></ProtectedRoute>} />
            <Route path="/fill-in" element={<ProtectedRoute><FillIn /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
            <Route path="/inquiries" element={<ProtectedRoute><Inquiries /></ProtectedRoute>} />
            <Route path="/pro" element={<ProtectedRoute><Pro /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </FadeMain>
      </Suspense>
      {showFooter && <Footer />}
    </>
  );
};

function App() {
  // MotionConfig used to wrap the whole tree, but importing framer-motion
  // at the App root forces its 46kB chunk into main.js for every page —
  // including auth/legal pages that don't animate. Each page that uses
  // motion.* now ships its own MotionConfig (currently only Home does).
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AdminModeProvider>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </AdminModeProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
